import React, { useEffect, useMemo } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { IArpMedia } from '../../../../../features/arp-registry/ArpMedia'
import CenteredLoadingAnimation from '../../../core/CenteredLoadingAnimation'
import { ConditionalWrapper } from '../../../core/ConditionalWrapper'
import SampleListItem from './SampleListItem'
import useKeyedList from './useKeyedList'

interface Props {
  samples: IArpMedia[]
  render?: ({ isOpen }: { isOpen: boolean; toggle: () => void }) => React.ReactElement
  defaultIsOpen?: boolean
  defaultIsActive?: boolean
  onItemClick?: () => void
  infiniteScroll?: {
    dataLength: number
    hasMore: boolean
    next: () => void
  }
}

const SampleList: React.FC<Props> = ({
  samples,
  render,
  defaultIsOpen = false,
  defaultIsActive = false,
  onItemClick = () => null,
  infiniteScroll
}) => {
  const [isOpen, setIsOpen] = React.useState(defaultIsOpen)
  const {
    isActive,
    setIsActive,
    highlightedIndex,
    setHighlightedIndex,
    selectedIndex,
    setSelectedIndex,
    ref
  } = useKeyedList<IArpMedia>({
    items: samples,
    defaultIsActive
  })

  useEffect(() => {
    setIsActive(defaultIsActive)
  }, [defaultIsActive])

  const handleToggle = () => {
    if (!isOpen) {
      setIsActive(true)
    }

    setHighlightedIndex(-1)
    setIsOpen((prev) => !prev)
  }

  const handleClick = () => {
    setIsActive(true)

    if (onItemClick) {
      onItemClick()
    }
  }

  const sortedSamples = useMemo(() => {
    return samples.sort((a, b) => a.metadata.title.localeCompare(b.metadata.title))
  }, samples)

  return (
    <>
      {render && render({ isOpen, toggle: handleToggle })}
      <ul
        ref={ref}
        className="ms-0 ps-0"
        style={{ listStyleType: 'none', display: isOpen ? 'initial' : 'none' }}
      >
        {isOpen && (
          <ConditionalWrapper
            condition={infiniteScroll !== undefined}
            wrapper={(children) => (
              <InfiniteScroll
                loader={<CenteredLoadingAnimation />}
                dataLength={infiniteScroll.dataLength}
                next={infiniteScroll.next}
                hasMore={infiniteScroll.hasMore}
                style={{ overflow: 'visible' }}
              >
                {children}
              </InfiniteScroll>
            )}
          >
            {sortedSamples.map((sampleData, index) => (
              <SampleListItem
                sampleData={sampleData}
                key={sampleData.id}
                index={index}
                highlightedIndex={highlightedIndex}
                setHighlightedIndex={setHighlightedIndex}
                selectedIndex={selectedIndex}
                setSelectedIndex={setSelectedIndex}
                onClick={handleClick}
                isActive={isActive}
              />
            ))}
          </ConditionalWrapper>
        )}
      </ul>
    </>
  )
}

export default SampleList
